/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { Table, type TableProps } from 'antd';
import { IOrder, IOrderItem } from '@/interfaces/order';
import TableComponent from '@/components/table';
import { formatNumber } from '@/utils/dataUtils';
import { round, sumBy } from 'lodash';
import { mediaView } from '@/utils/mediaUtils';
import { twMerge } from 'tailwind-merge';
import { DetailItem } from '@/components/dataItem/data-view-item';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/solid';

interface IProps {
  order: Partial<IOrder>;
  orderItems: Partial<IOrderItem>[];
  isPacking?: boolean;
}

const OrderItemTable = ({ orderItems, order, isPacking = false }: IProps) => {
  const columns: TableProps<Partial<IOrderItem>>['columns'] = [
    {
      title: 'รหัสสินค้า',
      dataIndex: 'productCode',
      width: 130,
      // className: 'font-bold',
      align: 'center',
    },
    {
      title: 'สินค้า',
      dataIndex: 'productName',
      // className: 'font-bold',
      render: _ => <div className="truncate">{_}</div>,
    },
    {
      title: 'Lot Number',
      dataIndex: 'lotNumber',
      width: 120,
      // className: 'font-bold',
      align: 'center',
    },
    {
      title: 'เลขที่ใบเสนอราคา',
      dataIndex: 'qtNo',
      width: 140,
      align: 'center',
      render: (_: number, rec) => (
        <div className="flex flex-row gap-3 cursor-pointer justify-center">
          <div
            className={twMerge(
              'flex items-center gap-1',
              !rec.qtNo && 'text-gray-400',
              rec.qtId && 'text-blue-600 underline',
            )}
            onClick={() => (rec.qtId ? mediaView({ id: rec.qtId }) : false)}
          >
            {_ ?? ''}
            {rec.qtId && (
              <ArrowTopRightOnSquareIcon className="w-4 h-4 text-blue-600" />
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'COA',
      dataIndex: 'coa',
      key: 'coa',
      align: 'center',
      width: 220,
      render: (_, rec) => (
        <div
          className={twMerge(
            'flex gap-1 items-center',
            rec.coaId && 'text-blue-600 underline',
            'cursor-pointer',
          )}
          onClick={() => (rec.coaId ? mediaView({ id: rec.coaId }) : false)}
        >
          {_ ?? 'ไม่พบไฟล์ COA'}
          {rec.coaId && (
            <ArrowTopRightOnSquareIcon className="w-4 h-4 text-blue-600" />
          )}
        </div>
      ),
    },
    {
      title: 'จำนวน Package',
      width: 120,
      dataIndex: 'package',
      align: 'right',
      render: (_: number, rec) =>
        formatNumber(
          round(Number(rec?.quantity) / Number(rec?.conversion), 2),
          2,
        ),
    },
    {
      title: 'หน่วย Package',
      width: 120,
      dataIndex: 'packageUnitName',
    },
    {
      title: 'จำนวน',
      width: 110,
      dataIndex: 'quantity',
      align: 'right',
      className: 'font-bold',
      render: (_: number) => formatNumber(_, 2),
    },
    {
      title: 'หน่วยขาย',
      width: 80,
      dataIndex: 'baseUnitName',
      className: 'font-bold',
      hidden: isPacking,
    },
    {
      title: 'FOC',
      width: 60,
      dataIndex: 'isFoc',
      className: 'font-bold',
      align: 'center',
      render: (_: boolean) => (
        <div className="font-bold w-full cursor-pointer text-blue-500">
          {(_ && 'FOC') || '-'}
        </div>
      ),
    },
    {
      title: 'ราคา (THB)',
      width: 120,
      dataIndex: 'unitPrice',
      align: 'right',
      className: 'font-bold text-green-700',
      render: (_: number, rec) => (
        <div className={twMerge(rec.isFoc && 'line-through')}>
          {formatNumber(_, 2)}
        </div>
      ),
      hidden: isPacking,
    },
    {
      title: 'รวมเงิน (THB)',
      width: 120,
      dataIndex: 'totalPrice',
      align: 'right',
      fixed: 'right',
      className: 'font-bold',
      render: (_: number, rec) => (
        <div className={twMerge(rec.isFoc && 'line-through')}>
          {formatNumber(
            round(Number(rec.quantity) * Number(rec.unitPrice), 2),
            2,
          )}
        </div>
      ),
      hidden: isPacking,
    },
    {
      title: `FX PRICE (${order?.currency})`,
      width: 120,
      dataIndex: 'alterUnitPrice',
      align: 'right',
      className: 'font-bold text-blue-700',
      render: (_: number, rec) => (
        <div className={twMerge(rec.isFoc && 'line-through')}>
          {formatNumber(_, 2)}
        </div>
      ),
      hidden: isPacking,
    },
    {
      title: `รวมเงิน (${order?.currency})`,
      width: 120,
      dataIndex: 'totalPrice',
      align: 'right',
      fixed: 'right',
      className: 'font-bold',
      hidden: isPacking,
      render: (_: number, rec) => (
        <div className={twMerge(rec.isFoc && 'line-through')}>
          {formatNumber(
            round(Number(rec.quantity) * Number(rec.alterUnitPrice), 2),
            2,
          )}
        </div>
      ),
    },
  ];

  const totalQuantity = sumBy(orderItems || [], 'quantity');
  const totalPrice = sumBy(orderItems || [], 'totalPrice');
  const alterTotalPrice = sumBy(orderItems || [], 'alterTotalPrice');

  return (
    <div className="flex flex-col gap-3">
      <TableComponent<Partial<IOrderItem>>
        columns={columns}
        criteria={{}}
        data={{
          data: orderItems,
          totalRecords: 0,
        }}
        loading={false}
        onTableChange={() => {
          console.log();
        }}
        scrollLength={1800}
        showPagination={false}
        summary={
          isPacking
            ? undefined
            : () => {
                return (
                  <Table.Summary fixed>
                    <Table.Summary.Row className="bg-red-100">
                      <Table.Summary.Cell
                        index={0}
                        colSpan={7}
                        className="font-bold items-end"
                        align="right"
                      >
                        จำนวนทั้งหมด
                      </Table.Summary.Cell>
                      <Table.Summary.Cell
                        index={0}
                        colSpan={1}
                        className="font-bold items-end"
                        align="right"
                      >
                        {formatNumber(totalQuantity || 0, 2)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell
                        index={0}
                        colSpan={3}
                        className="font-bold items-end"
                        align="right"
                      >
                        {`รวมเงิน`}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell
                        index={0}
                        colSpan={1}
                        className="font-bold items-end"
                        align="right"
                      >
                        {formatNumber(totalPrice || 0, 2)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell
                        index={0}
                        colSpan={1}
                        className="font-bold items-end"
                        align="right"
                      >
                        {`รวมเงิน`}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell
                        index={0}
                        colSpan={1}
                        className="font-bold items-end"
                        align="right"
                      >
                        {formatNumber(alterTotalPrice || 0, 2)}
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }
        }
      />
      {!isPacking && (
        <div className="flex justify-end md:flex-row flex-col gap-2">
          <div className="flex flex-col border border-red-400 rounded-lg p-2 text-bold w-[300px] bg-red-50">
            <DetailItem
              customMainClass="flex-row"
              customLabelClass="flex justify-end min-w-[140px]  text-sm"
              label="รวมเงิน"
              detail={`${formatNumber(order?.totalAmount || 0, 2)} THB`}
              customClass="flex justify-end w-full text-black font-bold  text-sm"
            />
            <DetailItem
              customMainClass="flex-row"
              customLabelClass="flex justify-end min-w-[140px] text-sm"
              label="ส่วนลด FOC"
              detail={`${formatNumber(Number(order?.discountAmountFOC), 2)} THB`}
              customClass="flex justify-end w-full text-black font-bold  text-sm"
            />
            <DetailItem
              customMainClass="flex-row"
              customLabelClass="flex justify-end min-w-[140px] text-sm"
              label="ส่วนลด"
              detail={`${formatNumber(Number(order?.discountAmount), 2)} THB`}
              customClass="flex justify-end w-full text-black font-bold  text-sm"
            />
            <DetailItem
              customMainClass="flex-row"
              customLabelClass="flex justify-end min-w-[140px] text-sm"
              label="รวมเงินหลังหักส่วนลด"
              detail={`${formatNumber(
                Number(order?.totalAfterDiscountAmount),
                2,
              )} THB`}
              customClass="flex justify-end w-full text-black font-bold text-sm"
            />
            <DetailItem
              customMainClass="flex-row"
              customLabelClass="flex justify-end min-w-[140px] text-sm"
              label={`VAT ${order?.vatRate} %`}
              detail={`${formatNumber(order?.vatAmount || 0, 2)} THB`}
              customClass="flex justify-end w-full text-black font-bold  text-sm"
            />
            <DetailItem
              customMainClass="flex-row"
              customLabelClass="flex justify-end min-w-[140px]   text-sm"
              label="ยอดสุทธิ"
              detail={`${formatNumber(order?.summary || 0, 2)} THB`}
              customClass="flex justify-end w-full text-black font-bold  text-sm"
            />
          </div>
          <div className="flex flex-col border border-blue-400 rounded-lg p-2 text-bold w-[290px] bg-blue-50">
            <DetailItem
              customMainClass="flex-row"
              customLabelClass="flex justify-end min-w-[140px]  text-sm"
              label="รวมเงิน"
              detail={`${formatNumber(order?.totalAmountAlter || 0, 2)} ${order?.currency}`}
              customClass="flex justify-end w-full text-black font-bold  text-sm"
            />
            <DetailItem
              customMainClass="flex-row"
              customLabelClass="flex justify-end min-w-[140px] text-sm"
              label="ส่วนลด FOC"
              detail={`${formatNumber(Number(order?.discountAmountAlterFOC), 2)} ${order?.currency}`}
              customClass="flex justify-end w-full text-black font-bold  text-sm"
            />
            <DetailItem
              customMainClass="flex-row"
              customLabelClass="flex justify-end min-w-[140px] text-sm"
              label="ส่วนลด"
              detail={`${formatNumber(Number(order?.discountAmountAlter), 2)} ${order?.currency}`}
              customClass="flex justify-end w-full text-black font-bold  text-sm"
            />
            <DetailItem
              customMainClass="flex-row"
              customLabelClass="flex justify-end min-w-[140px] text-sm"
              label="รวมเงินหลังหักส่วนลด"
              detail={`${formatNumber(
                Number(order?.totalAfterDiscountAmountAlter),
                2,
              )} ${order?.currency}`}
              customClass="flex justify-end w-full text-black font-bold text-sm"
            />
            <DetailItem
              customMainClass="flex-row"
              customLabelClass="flex justify-end min-w-[140px] text-sm"
              label={`VAT ${order?.vatRate} %`}
              detail={`${formatNumber(order?.vatAmountAlter || 0, 2)} ${order?.currency}`}
              customClass="flex justify-end w-full text-black font-bold  text-sm"
            />
            <DetailItem
              customMainClass="flex-row"
              customLabelClass="flex justify-end min-w-[140px]   text-sm"
              label="ยอดสุทธิ"
              detail={`${formatNumber(order?.summaryAlter || 0, 2)} ${order?.currency}`}
              customClass="flex justify-end w-full text-black font-bold  text-sm"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderItemTable;
